/**
 * The units "About this page" search ranks: one chunk per task, per language.
 *
 * The same function builds the chunks the embedding script embeds
 * (scripts/build-page-info-embeddings.ts) and the chunks searched at run time,
 * so the text a vector stands for is exactly the text shown. Each chunk carries
 * a hash of that text; the shipped index records the hash it embedded, and a
 * test fails when copy changes without the index being rebuilt.
 */
import type { Translations } from "@/lib/i18n/types";
import type { PageInfoId, PageInfoItemCopy } from "@/lib/i18n/types/pageInfo";
import { PAGE_INFO, type PageInfoItemDef } from "../pageInfoRegistry";

export interface PageInfoChunk {
  /** `page.item` — stable across languages. */
  key: string;
  page: PageInfoId;
  item: string;
  pageTitle: string;
  title: string;
  points: string[];
  /** What is embedded and keyword-searched. */
  text: string;
  hash: string;
}

/** FNV-1a, 32-bit. Detects edited copy; not a security boundary. */
export function hashText(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function buildPageInfoChunks(t: Translations): PageInfoChunk[] {
  const chunks: PageInfoChunk[] = [];
  for (const page of Object.keys(PAGE_INFO) as PageInfoId[]) {
    const pageTitle = (PAGE_INFO[page].searchTitle ?? PAGE_INFO[page].title)(t);
    const copy = t.pageInfo.pages[page];
    const items = copy.items as Record<string, PageInfoItemCopy>;
    for (const def of PAGE_INFO[page].items as ReadonlyArray<PageInfoItemDef<PageInfoId>>) {
      const { title, points } = items[def.id];
      const text = `${pageTitle}. ${title}. ${points.join(" ")}`;
      chunks.push({
        key: `${page}.${def.id}`,
        page,
        item: def.id,
        pageTitle,
        title,
        points,
        text,
        hash: hashText(text),
      });
    }
  }
  return chunks;
}
